/**
 * FoodPulseLabelScan — take a photo of a food label, we tell you
 * what's inside. Powered by Gemini vision + the FoodPulse Ingredient
 * Encyclopedia.
 *
 * Route:   /food-pulse/label-scan
 * Backend: POST /api/label-scan/analyze
 *
 * Flow:
 *   1. Camera capture OR file pick (mobile-first)
 *   2. POST as multipart, show a loader
 *   3. Render: product name → ingredient chips (encyclopedia-matched
 *      chips are terracotta + linked) → nutrition mini-panel
 */
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Camera, Upload, Loader2, AlertTriangle, CheckCircle2, X, RefreshCw,
  Search, Type, ArrowRight, Sparkles, Package,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;
const CREAM = "#FBF7EF";
const AMBER = "#E4A140";
const ORANGE = "#EA580C";
const DARK = "#1A1613";

export default function FoodPulseLabelScan() {
  const { user, loading: authLoading } = useAuth();
  const [mode, setMode] = useState("photo");   // photo | name
  const [file,    setFile]    = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [result,  setResult]  = useState(null);
  const [propagatedMsg, setPropagatedMsg] = useState(null);
  const [nameQ,  setNameQ]  = useState("");
  const [brandQ, setBrandQ] = useState("");
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  function pickFile(f) {
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      toast.error("Please pick an image file");
      return;
    }
    setFile(f);
    setResult(null);
    setPropagatedMsg(null);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  }

  async function analyse() {
    if (!file) { toast.error("Pick or take a photo first"); return; }
    setBusy(true);
    setPropagatedMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await http.post("/label-scan/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setResult(res.data);
      if (res.data.matched_count > 0) {
        toast.success(`Matched ${res.data.matched_count} ingredient${res.data.matched_count === 1 ? "" : "s"} in the encyclopedia`);
      } else {
        toast.info("Label read. No encyclopedia matches yet — we'll surface entries as we add them.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read that label. Try a clearer photo.");
    } finally {
      setBusy(false);
    }
  }

  async function lookupByName() {
    const name = nameQ.trim();
    if (name.length < 2) { toast.error("Enter a product name (2+ characters)"); return; }
    setBusy(true);
    setPropagatedMsg(null);
    try {
      const res = await http.post("/label-scan/lookup", {
        product_name: name, brand: brandQ.trim() || null,
      }, { timeout: 120000 });
      const data = res.data;
      setResult({
        ok: true,
        product_name:    data.product?.product_name,
        brand:           data.product?.brand,
        ingredients_raw: data.product?.ingredients_raw,
        // Backfill matched slugs / raw pills from stored ingredients list
        ingredients: (data.product?.ingredients || []).map((raw, i) => {
          const slug = (data.product?.matched_slugs || [])[i] || null;
          return { raw, matched_slug: slug };
        }),
        matched_count:    data.matched_count || (data.product?.matched_slugs || []).length,
        allergen_warnings: data.product?.allergen_warnings || [],
        nutrition:        data.product?.nutrition || {},
        confidence:       data.product?.confidence || "medium",
      });
      if (data.propagated) {
        setPropagatedMsg(data.message ||
          "We didn't have this product yet — FoodPulse has just propagated a best-guess record. Take a real photo to lock in the details.");
      } else {
        toast.success("Found it in the FoodPulse database.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't look up that product. Try again.");
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setFile(null); setPreview(null); setResult(null);
    setNameQ(""); setBrandQ(""); setPropagatedMsg(null);
  }

  if (!authLoading && !user) {
    return (
      <div className="min-h-screen" style={{ background: CREAM }}>
        <SeoHead section="food-pulse"
                 title="Label Scanner — Food Pulse"
                 description="Snap a photo of any food packaging. We'll read the ingredient list and explain what's inside." />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-6">
          <div className="text-center bg-white rounded-2xl border p-8 shadow-sm"
               style={{ borderColor: AMBER }} data-testid="label-scan-signin">
            <Camera className="w-14 h-14 mx-auto mb-4" style={{ color: ORANGE }} />
            <h1 className="font-display text-2xl mb-2" style={{ color: DARK }}>Sign in to scan food labels</h1>
            <p className="text-sm opacity-80 mb-6 max-w-md mx-auto">
              We use AI vision to read every ingredient off a photograph and
              match it to our encyclopedia so you know exactly what's inside.
            </p>
            <Link to="/login?next=/food-pulse/label-scan"
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-white font-semibold"
                  style={{ background: ORANGE }}
                  data-testid="label-scan-signin-cta">Sign in →</Link>
          </div>
        </div>
        {/* Preview of what's already inside the FoodPulse product DB */}
        <ProductsPreviewRibbon />
        <LabelScannerAbout />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: DARK }} data-testid="label-scan-page">
      <SeoHead section="food-pulse"
               title="Label Scanner — Food Pulse"
               description="Snap a photo of any food packaging. We'll read the ingredient list and explain what's inside." />

      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: ORANGE }}>
          Food Pulse · What's in your food
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mb-3">
          Snap a label. Search a name.
        </h1>
        <p className="opacity-80 max-w-xl mb-6 text-sm sm:text-base">
          Point your camera at any food package — or just type the product
          name. We'll pull ingredients, allergens and nutrition from the
          FoodPulse database, and propagate new entries automatically when
          they're missing.
        </p>

        {/* Search existing catalogue */}
        <CatalogueSearchBar />

        {/* Mode toggle */}
        {!preview && !result && (
          <div className="mt-8 flex gap-1 rounded-full border bg-white p-1 max-w-md"
               style={{ borderColor: AMBER }} data-testid="label-scan-mode">
            <button type="button" onClick={() => setMode("photo")}
                    className="flex-1 rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 justify-center transition-all"
                    style={mode === "photo" ? { background: ORANGE, color: "#fff" } : { color: ORANGE }}
                    data-testid="label-scan-mode-photo">
              <Camera className="w-3.5 h-3.5" /> Photo / upload
            </button>
            <button type="button" onClick={() => setMode("name")}
                    className="flex-1 rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 justify-center transition-all"
                    style={mode === "name" ? { background: ORANGE, color: "#fff" } : { color: ORANGE }}
                    data-testid="label-scan-mode-name">
              <Type className="w-3.5 h-3.5" /> Search by name
            </button>
          </div>
        )}

        {/* Photo capture panel */}
        {mode === "photo" && !preview && !result && (
          <div className="rounded-2xl border-2 border-dashed p-8 text-center mt-4"
               style={{ borderColor: AMBER, background: "#fff" }}
               data-testid="label-scan-capture">
            <Camera className="w-10 h-10 mx-auto mb-3 opacity-60" style={{ color: ORANGE }} />
            <div className="font-display text-xl mb-4">Add a photograph of the label</div>
            <p className="text-xs opacity-70 mb-6 max-w-md mx-auto">
              Straight-on shots of the ingredient list work best. Or upload a
              photo you've already saved on this device.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <input ref={cameraInputRef}
                     type="file" accept="image/*" capture="environment"
                     onChange={(e) => pickFile(e.target.files?.[0])}
                     className="hidden"
                     data-testid="label-scan-camera-input" />
              <input ref={fileInputRef}
                     type="file" accept="image/*"
                     onChange={(e) => pickFile(e.target.files?.[0])}
                     className="hidden"
                     data-testid="label-scan-file-input" />
              <button onClick={() => cameraInputRef.current?.click()}
                      className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-semibold"
                      style={{ background: ORANGE }}
                      data-testid="label-scan-take-photo">
                <Camera className="w-4 h-4" /> Take a photo
              </button>
              <button onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold border-2"
                      style={{ borderColor: ORANGE, color: ORANGE }}
                      data-testid="label-scan-pick-file">
                <Upload className="w-4 h-4" /> Upload a file
              </button>
            </div>
          </div>
        )}

        {/* Name-lookup panel */}
        {mode === "name" && !result && (
          <div className="rounded-2xl border p-6 sm:p-8 mt-4 bg-white"
               style={{ borderColor: AMBER }} data-testid="label-scan-name-panel">
            <div className="font-display text-xl mb-1">Search by product name</div>
            <p className="text-xs opacity-70 mb-4 max-w-md">
              Type what's on the package. If we already have it we'll show
              you instantly — if not, we'll propagate a starter entry using
              open reference data.
            </p>
            <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
              <label className="block">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">
                  Product name
                </div>
                <input type="text" value={nameQ}
                       onChange={(e) => setNameQ(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && lookupByName()}
                       placeholder="Corn Flakes"
                       className="w-full rounded-lg border px-3 py-2 text-sm"
                       style={{ borderColor: "#E9D8B4" }}
                       data-testid="label-scan-name-input" />
              </label>
              <label className="block">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">
                  Brand (optional)
                </div>
                <input type="text" value={brandQ}
                       onChange={(e) => setBrandQ(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && lookupByName()}
                       placeholder="Kellogg's"
                       className="w-full rounded-lg border px-3 py-2 text-sm"
                       style={{ borderColor: "#E9D8B4" }}
                       data-testid="label-scan-brand-input" />
              </label>
              <button onClick={lookupByName} disabled={busy || nameQ.trim().length < 2}
                      className="self-end inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-semibold disabled:opacity-50 hover:shadow-md transition-shadow"
                      style={{ background: ORANGE }}
                      data-testid="label-scan-lookup-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {busy ? "Looking…" : "Look up"}
              </button>
            </div>
            <div className="mt-3 text-[10px] opacity-60 inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> New products are propagated in seconds.
            </div>
          </div>
        )}

        {/* Preview + submit */}
        {preview && !result && (
          <div className="rounded-2xl border bg-white p-4 sm:p-5 mt-4" style={{ borderColor: AMBER }}
               data-testid="label-scan-preview">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <img src={preview} alt="Label preview"
                   className="w-40 h-40 object-cover rounded-xl border" style={{ borderColor: "#E9D8B4" }} />
              <div className="flex-1 min-w-0 text-center sm:text-left">
                <div className="font-display text-lg mb-1">Ready to analyse</div>
                <p className="text-xs opacity-70 mb-4">{file?.name} · {(file?.size / 1024).toFixed(0)} KB</p>
                <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                  <button onClick={analyse} disabled={busy}
                          className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-white font-semibold disabled:opacity-60"
                          style={{ background: ORANGE }}
                          data-testid="label-scan-analyze-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                    {busy ? "Reading label…" : "Analyse label"}
                  </button>
                  <button onClick={reset}
                          className="inline-flex items-center gap-2 rounded-full px-4 py-2 border font-semibold"
                          style={{ borderColor: "#E9D8B4" }}
                          data-testid="label-scan-reset-btn">
                    <X className="w-4 h-4" /> Change photo
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Propagation notice */}
        {propagatedMsg && (
          <div className="mt-6 rounded-2xl border p-4 flex gap-3"
               style={{ borderColor: AMBER, background: "#FFF9EA" }}
               data-testid="label-scan-propagated-notice">
            <Sparkles className="w-5 h-5 flex-none mt-0.5" style={{ color: "#8B5E10" }} />
            <div className="text-sm opacity-90">{propagatedMsg}</div>
          </div>
        )}

        {/* Results */}
        {result && <ScanResult result={result} preview={preview} onReset={reset} />}
      </div>

      {/* Products already in the DB — inspiration for what to try next */}
      {!result && <ProductsPreviewRibbon />}

      {/* Educational article — visible to signed-in users too, below the tool */}
      {!result && <LabelScannerAbout />}
    </div>
  );
}


// ─── Search-existing bar ────────────────────────────────────────────
function CatalogueSearchBar() {
  const [q, setQ] = useState("");

  function submit(e) {
    e?.preventDefault();
    const query = q.trim();
    if (!query) return;
    // Delegate to the products directory search
    window.location.href = `/food-pulse/products?q=${encodeURIComponent(query)}`;
  }

  return (
    <form onSubmit={submit}
          className="rounded-full bg-white border flex items-center gap-2 px-3 py-1.5 shadow-sm max-w-xl"
          style={{ borderColor: AMBER }}
          data-testid="label-scan-catalogue-search">
      <Search className="w-4 h-4 opacity-60" />
      <input type="search" value={q}
             onChange={(e) => setQ(e.target.value)}
             placeholder="Search product name or ingredient — e.g. 'kitkat', 'MSG'"
             className="flex-1 bg-transparent outline-none text-sm py-1"
             data-testid="label-scan-catalogue-input" />
      <button type="submit"
              className="rounded-full px-4 py-1.5 text-xs font-semibold text-white"
              style={{ background: ORANGE }}
              data-testid="label-scan-catalogue-submit">
        Search catalogue
      </button>
    </form>
  );
}


// ─── Products already in the DB (ribbon) ────────────────────────────
function ProductsPreviewRibbon() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/label-scan/products`, { params: { limit: 8, order: "popular" } })
           .then((r) => r.data.items).catch(() => []),
      axios.get(`${API}/api/label-scan/stats`).then((r) => r.data).catch(() => null),
    ]).then(([its, st]) => {
      setItems(its || []);
      setStats(st);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <section className="border-t" style={{ borderColor: "#E9D8B4", background: "#F0E2C7" }}
             data-testid="label-scan-preview-ribbon">
      <div className="max-w-6xl mx-auto px-4 py-10 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
                 style={{ color: "#B25D38" }}>
              Already in the FoodPulse database
            </div>
            <h2 className="font-display text-2xl sm:text-3xl leading-tight" style={{ color: "#3E2A0A" }}>
              What we already know about.
            </h2>
            {stats && (
              <p className="text-sm opacity-80 mt-1">
                <strong>{stats.products.toLocaleString()}</strong> products indexed ·
                <strong> {stats.unique_brands.toLocaleString()}</strong> unique brands ·
                <strong> {stats.unmatched_ingredients.toLocaleString()}</strong> ingredients pending review
              </p>
            )}
          </div>
          <Link to="/food-pulse/products"
                className="inline-flex items-center gap-1 text-sm font-semibold hover:underline"
                style={{ color: "#8B5E10" }}
                data-testid="label-scan-preview-see-all">
            Browse all products <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="text-sm opacity-70 inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading products…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed p-8 text-center"
               style={{ borderColor: "#C79A3F", background: "#FFF9EA" }}>
            <Package className="w-8 h-8 mx-auto mb-2 opacity-40" style={{ color: "#8B5E10" }} />
            <div className="text-sm opacity-80">
              The catalogue is just getting started. Scan a product to make it the first!
            </div>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-3 -mx-4 px-4 snap-x snap-mandatory"
               data-testid="label-scan-preview-items">
            {items.map((p) => (
              <Link key={p.id} to={`/food-pulse/products/${p.id}`}
                    className="snap-start flex-none w-52 rounded-2xl border bg-white p-4 hover:shadow-md transition-all hover:-translate-y-0.5"
                    style={{ borderColor: "#E9D8B4" }}
                    data-testid={`label-scan-preview-item-${p.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <Package className="w-4 h-4 opacity-60" style={{ color: "#8B5E10" }} />
                  {p.scan_count > 1 && (
                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-full"
                          style={{ background: "#F0E2C7", color: "#8B5E10" }}>
                      {p.scan_count}×
                    </span>
                  )}
                </div>
                {p.brand && (
                  <div className="text-[9px] font-mono uppercase tracking-widest opacity-70">
                    {p.brand}
                  </div>
                )}
                <div className="font-display text-sm leading-tight mt-0.5 line-clamp-2"
                     style={{ color: "#3E2A0A" }}>
                  {p.product_name}
                </div>
                <div className="mt-3 text-[10px] opacity-70">
                  {(p.matched_slugs || []).length} matched · {(p.ingredients || []).length} total
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}


function LabelScannerAbout() {
  return (
    <section className="border-t" style={{ borderColor: "#E9D8B4", background: "#FFFDF7" }}
             data-testid="label-scan-about">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
             style={{ color: ORANGE }}>
          About the Label Scanner
        </div>
        <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-2"
            style={{ color: DARK }}>
          FoodPulse Label Scanner
        </h2>
        <p className="font-display text-lg opacity-80 mb-6" style={{ color: DARK }}>
          Know What's Inside Every Product
        </p>

        <article className="prose prose-neutral max-w-none prose-headings:font-display prose-h3:mt-6 prose-h3:mb-2 prose-h2:mt-8 prose-h2:mb-3 prose-p:leading-relaxed prose-ul:my-3 prose-li:my-0.5"
                 style={{ color: "#3E2A0A" }}>
          <p>Every day, millions of people purchase packaged foods without fully understanding what they are eating. Ingredient lists can be difficult to interpret, nutrition labels often require careful comparison, and unfamiliar additives, preservatives, sweeteners, and processing methods can make it challenging for consumers to make informed decisions.</p>
          <p><strong>The FoodPulse Label Scanner was created to change that.</strong></p>
          <p>Our mission is simple: <em>help everyone understand exactly what they are buying and eating.</em></p>
          <p>Using your smartphone or computer, simply scan a product's barcode or food label. The FoodPulse Label Scanner identifies the product and instantly compares its ingredients and nutritional information with the comprehensive FoodPulse food knowledge database. Within seconds, it generates an easy-to-understand report that explains what's inside the product and what it may mean for your health and lifestyle.</p>
          <p>Rather than simply displaying a nutrition panel, FoodPulse translates complex food information into meaningful insights. It explains ingredients in plain language, highlights allergens, identifies food additives and preservatives, evaluates nutritional quality, and provides balanced information based on scientific research and publicly available data. Our goal is to help consumers understand — not to replace professional medical advice or make personal health decisions for them.</p>

          <h2>Our Mission</h2>
          <p>FoodPulse believes that <strong>knowledge empowers healthier choices</strong>.</p>
          <p>Consumers deserve clear, transparent, and accessible information about the foods they purchase. By making food information easier to understand, we hope to encourage healthier eating habits, promote greater transparency within the food industry, reduce misinformation, and support better long-term public health.</p>
          <p>The Label Scanner is designed to answer important questions such as:</p>
          <ul>
            <li>What ingredients are in this product?</li>
            <li>What does each ingredient do?</li>
            <li>Does it contain artificial additives or preservatives?</li>
            <li>Are there common allergens?</li>
            <li>How nutritious is this product?</li>
            <li>Is it high in sugar, sodium, or saturated fat?</li>
            <li>Does it contain artificial colors, flavors, or sweeteners?</li>
            <li>How processed is this food?</li>
            <li>How does it compare with similar products?</li>
            <li>Are there healthier alternatives?</li>
          </ul>
          <p>Instead of expecting consumers to research every ingredient individually, FoodPulse brings that information together into one comprehensive, easy-to-read report.</p>

          <h2>What the Label Scanner Analyzes</h2>
          <p>The FoodPulse Label Scanner compares product information against our growing knowledge base, including:</p>
          <ul>
            <li>Product identification</li>
            <li>Ingredient lists</li>
            <li>Nutrition Facts</li>
            <li>Serving sizes</li>
            <li>Calories</li>
            <li>Protein, carbohydrates, fats, fiber, and sugars</li>
            <li>Vitamins and minerals</li>
            <li>Allergens</li>
            <li>Food additives</li>
            <li>Preservatives</li>
            <li>Artificial colors</li>
            <li>Artificial flavors</li>
            <li>Sweeteners</li>
            <li>Processing methods (where available)</li>
            <li>Country of origin (when available)</li>
            <li>Manufacturer information</li>
            <li>Dietary suitability (such as vegetarian or vegan, where applicable)</li>
            <li>Certifications (such as organic or gluten-free, where available)</li>
          </ul>
          <p>Each ingredient is explained in language that is easy for consumers to understand.</p>

          <h2>Personalized Understanding</h2>
          <p>People have different nutritional goals and dietary needs. The Label Scanner is designed to help users interpret information in the context of their own preferences, whether they are looking to reduce added sugars, limit sodium, avoid certain allergens, or increase protein intake. The reports provide information and context so that users can make informed choices based on their own priorities.</p>

          <h2>Compare Before You Buy</h2>
          <p>One of the most powerful features of the FoodPulse Label Scanner is <strong>product comparison</strong>.</p>
          <p>Consumers can compare similar products side by side to evaluate differences in:</p>
          <ul>
            <li>Nutrition</li>
            <li>Ingredients</li>
            <li>Sugar content</li>
            <li>Sodium</li>
            <li>Protein</li>
            <li>Fiber</li>
            <li>Additives</li>
            <li>Serving size</li>
            <li>Overall nutritional profile</li>
          </ul>
          <p>This makes it easier to choose the product that best aligns with individual preferences and dietary goals.</p>

          <h2>Transparency Builds Trust</h2>
          <p>FoodPulse supports transparency throughout the food supply chain.</p>
          <p>By making ingredient and nutrition information easier to access and understand, we encourage informed purchasing decisions while helping consumers better understand the products available to them. We also provide manufacturers with an opportunity to present accurate product information and updates.</p>

          <h2>Powered by Community Knowledge</h2>
          <p>The FoodPulse database grows every day through contributions from manufacturers, retailers, nutrition experts, researchers, food professionals, and consumers.</p>
          <p>Community members can:</p>
          <ul>
            <li>Report incorrect information</li>
            <li>Submit new products</li>
            <li>Upload clearer label photos</li>
            <li>Suggest corrections</li>
            <li>Add missing ingredient information</li>
            <li>Share product reviews</li>
            <li>Contribute research references</li>
          </ul>
          <p>Together, we are building one of the world's most comprehensive food information resources.</p>

          <h2>Helping Consumers Make Informed Choices</h2>
          <p>The FoodPulse Label Scanner is designed for anyone who wants to better understand packaged foods — from parents shopping for their families to students, athletes, seniors, and anyone interested in learning more about the products they buy.</p>
          <p>Our reports are intended to provide educational information based on product labels and trusted reference sources. They are not a substitute for advice from qualified healthcare professionals, especially for individuals with medical conditions, allergies, or specialized dietary needs.</p>
          <p>Every scan is an opportunity to learn more about your food. Every informed purchase encourages greater transparency and helps create a food system where knowledge is readily available to everyone.</p>

          <h2>Our Vision</h2>
          <p>We envision a future where every food product can be scanned and understood in seconds, giving consumers the confidence to make informed decisions. By combining technology, scientific knowledge, and community contributions, FoodPulse aims to build the world's most trusted food intelligence platform — one that promotes transparency, encourages healthier choices, supports responsible manufacturing, and helps people understand the story behind every product they bring home.</p>
        </article>

        <div className="mt-8 flex justify-center">
          <Link to="/login?next=/food-pulse/label-scan"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-white font-semibold hover:shadow-md transition-shadow"
                style={{ background: ORANGE }}
                data-testid="label-scan-about-cta">
            Sign in →
          </Link>
        </div>
      </div>
    </section>
  );
}


function ScanResult({ result, preview, onReset }) {
  const nutrition = result.nutrition || {};
  const nutritionRows = [
    ["Serving size",    nutrition.serving_size],
    ["Calories",        nutrition.calories],
    ["Total fat",       nutrition.total_fat],
    ["Saturated fat",   nutrition.saturated_fat],
    ["Trans fat",       nutrition.trans_fat],
    ["Cholesterol",     nutrition.cholesterol],
    ["Sodium",          nutrition.sodium],
    ["Total carb",      nutrition.total_carb],
    ["Fiber",           nutrition.dietary_fiber],
    ["Sugars",          nutrition.sugars],
    ["Added sugars",    nutrition.added_sugars],
    ["Protein",         nutrition.protein],
  ].filter(([, v]) => v);

  return (
    <div className="rounded-2xl border bg-white overflow-hidden" style={{ borderColor: AMBER }}
         data-testid="label-scan-result">
      <div className="grid gap-4 sm:grid-cols-[160px_1fr] p-5 border-b" style={{ borderColor: "#E9D8B4" }}>
        {preview && (
          <img src={preview} alt="Scanned label"
               className="w-full max-w-[160px] rounded-xl object-cover border justify-self-center"
               style={{ borderColor: "#E9D8B4" }} />
        )}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: ORANGE }}>
            {result.brand || "Product"} · Confidence: {result.confidence || "medium"}
          </div>
          <h2 className="font-display text-2xl leading-tight mt-1" data-testid="label-scan-product-name">
            {result.product_name || "Unnamed product"}
          </h2>
          {result.allergen_warnings?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {result.allergen_warnings.map((w) => (
                <span key={w} className="text-[11px] px-2 py-0.5 rounded-full"
                      style={{ background: "#FEE7DA", color: "#7A3E00" }}>
                  <AlertTriangle className="w-3 h-3 inline mr-1" /> {w}
                </span>
              ))}
            </div>
          )}
          <button onClick={onReset}
                  className="mt-4 inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100">
            <RefreshCw className="w-3 h-3" /> Scan another
          </button>
        </div>
      </div>

      {/* Ingredients */}
      <div className="p-5 border-b" style={{ borderColor: "#E9D8B4" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-lg">Ingredients</div>
          <div className="text-[11px] font-mono opacity-70">
            {result.matched_count} of {result.ingredients.length} in our encyclopedia
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {result.ingredients.map((ing, idx) => {
            const matched = !!ing.matched_slug;
            const label = (ing.raw || ing.matched_name || "").trim();
            const pill = (
              <span
                key={idx}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs border transition-all ${
                  matched ? "font-semibold hover:shadow-sm" : "opacity-80"
                }`}
                style={
                  matched
                    ? { background: "#FFF3E0", borderColor: ORANGE, color: "#7A3E00" }
                    : { background: "#F8F4EA", borderColor: "#E9D8B4", color: "#4B3A20" }
                }
                data-testid={matched ? `label-ing-match-${ing.matched_slug}` : `label-ing-raw-${idx}`}
              >
                {label}
                {matched && ing.e_number && (
                  <span className="text-[9px] font-mono px-1 rounded"
                        style={{ background: ORANGE, color: "#fff" }}>{ing.e_number}</span>
                )}
                {matched && ing.has_safety_alert && (
                  <AlertTriangle className="w-3 h-3" style={{ color: "#B23838" }} />
                )}
                {matched && <span aria-hidden>→</span>}
              </span>
            );
            return matched
              ? <Link key={idx} to={`/food-pulse/encyclopedia/${ing.matched_slug}`}
                      title={ing.summary || ing.matched_name}>{pill}</Link>
              : pill;
          })}
        </div>
        {result.matched_count > 0 && (
          <div className="mt-4 text-xs rounded-lg p-3 flex items-start gap-2"
               style={{ background: "#F0FDF4", color: "#166534" }}>
            <CheckCircle2 className="w-4 h-4 flex-none mt-0.5" />
            <span>
              Tap any highlighted ingredient to open its encyclopedia entry — full history,
              health risks, regulatory status, and safer alternatives.
            </span>
          </div>
        )}
      </div>

      {/* Nutrition */}
      {nutritionRows.length > 0 && (
        <div className="p-5">
          <div className="font-display text-lg mb-3">Nutrition facts</div>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {nutritionRows.map(([k, v]) => (
              <div key={k} className="rounded-lg border p-3" style={{ borderColor: "#E9D8B4" }}>
                <dt className="text-[10px] font-mono uppercase tracking-widest opacity-60">{k}</dt>
                <dd className="text-sm font-semibold mt-0.5">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
